import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../App";

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function EmployeeList() {
	const [employees, setEmployees] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [editingEmployee, setEditingEmployee] = useState(null);
	const [formData, setFormData] = useState({
		firstName: "",
		username: "",
		password: "",
		shiftType: "day",
		isSupervisor: false,
		departments: [],
		daysOff: [],
	});
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

	useEffect(() => {
		loadData();
	}, []);

	const loadData = async () => {
		try {
			const [empRes, deptRes] = await Promise.all([
				axios.get(`${API_URL}/employees`),
				axios.get(`${API_URL}/departments`),
			]);
			setEmployees(empRes.data);
			setDepartments(deptRes.data);
		} catch (error) {
			setError("Failed to load data");
		} finally {
			setLoading(false);
		}
	};

	const openModal = (employee = null) => {
		if (employee) {
			setEditingEmployee(employee);
			setFormData({
				firstName: employee.first_name,
				username: employee.username || "",
				password: "",
				shiftType: employee.shift_type || "day",
				isSupervisor: !!employee.is_supervisor,
				departments: employee.departments?.map((d) => d.id) || [],
				daysOff: employee.daysOff || [],
			});
		} else {
			setEditingEmployee(null);
			setFormData({
				firstName: "",
				username: "",
				password: "",
				shiftType: "day",
				isSupervisor: false,
				departments: [],
				daysOff: [],
			});
		}
		setShowModal(true);
		setError("");
	};

	const closeModal = () => {
		setShowModal(false);
		setEditingEmployee(null);
		setError("");
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");

		try {
			const payload = {
				firstName: formData.firstName,
				shiftType: formData.shiftType,
				isSupervisor: formData.isSupervisor,
				departments: formData.departments,
				daysOff: formData.daysOff,
			};

			if (!editingEmployee && formData.username && formData.password) {
				payload.username = formData.username;
				payload.password = formData.password;
			}

			if (editingEmployee) {
				// Only sent when filled in; a blank password keeps the current one
				if (formData.username.trim()) {
					payload.username = formData.username.trim();
				}
				if (formData.password) payload.password = formData.password;
				await axios.put(
					`${API_URL}/employees/${editingEmployee.id}`,
					payload,
				);
				setSuccess("Employee updated successfully");
			} else {
				await axios.post(`${API_URL}/employees`, payload);
				setSuccess("Employee created successfully");
			}

			closeModal();
			loadData();
			setTimeout(() => setSuccess(""), 3000);
		} catch (error) {
			setError(error.response?.data?.error || "Failed to save employee");
		}
	};

	const handleDelete = async (id) => {
		try {
			await axios.delete(`${API_URL}/employees/${id}`);
			setSuccess("Employee deleted successfully");
			loadData();
			setTimeout(() => setSuccess(""), 3000);
		} catch (error) {
			setError("Failed to delete employee");
		}
	};

	const toggleDepartment = (deptId) => {
		setFormData((prev) => ({
			...prev,
			departments: prev.departments.includes(deptId)
				? prev.departments.filter((id) => id !== deptId)
				: [...prev.departments, deptId],
		}));
	};

	const toggleDayOff = (weekNumber, dayOfWeek) => {
		setFormData((prev) => {
			const existing = prev.daysOff.find(
				(d) => d.weekNumber === weekNumber && d.dayOfWeek === dayOfWeek,
			);
			if (existing) {
				return {
					...prev,
					daysOff: prev.daysOff.filter(
						(d) =>
							!(
								d.weekNumber === weekNumber &&
								d.dayOfWeek === dayOfWeek
							),
					),
				};
			} else {
				return {
					...prev,
					daysOff: [...prev.daysOff, { weekNumber, dayOfWeek }],
				};
			}
		});
	};

	const isDayOffSelected = (weekNumber, dayOfWeek) => {
		return formData.daysOff.some(
			(d) => d.weekNumber === weekNumber && d.dayOfWeek === dayOfWeek,
		);
	};

	if (loading) {
		return (
			<div className='loading-container'>
				<div className='spinner'></div>
			</div>
		);
	}

	return (
		<div>
			<div className='page-header'>
				<h1 className='page-title'>Employees</h1>
				<button className='btn btn-primary' onClick={() => openModal()}>
					+ Add Employee
				</button>
			</div>

			{success && <div className='alert alert-success'>{success}</div>}
			{error && <div className='alert alert-error'>{error}</div>}

			<div className='card'>
				<div className='table-container'>
					<table className='table'>
						<thead>
							<tr>
								<th>Name</th>
								<th>Departments</th>
								<th>Weekend Freq</th>
								<th>Shift Type</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{employees.length === 0 ? (
								<tr>
									<td
										colSpan='5'
										className='text-center text-muted'
										style={{ padding: "2rem" }}
									>
										No employees found. Add your first
										employee to get started.
									</td>
								</tr>
							) : (
								employees.map((emp) => (
									<tr key={emp.id}>
										<td>
											<div className='font-medium'>
												{emp.first_name}
											</div>
											{emp.is_supervisor ? (
												<span
													className='badge'
													style={{
														background:
															"var(--warning-100)",
														color: "var(--warning-700)",
														fontSize: "0.65rem",
														marginTop: "2px",
													}}
												>
													Supervisor
												</span>
											) : null}{" "}
										</td>
										<td>
											<div
												className='flex gap-sm'
												style={{ flexWrap: "wrap" }}
											>
												{emp.departments?.map((d) => (
													<span
														key={d.id}
														className='badge badge-primary'
													>
														{d.name}
													</span>
												)) || "-"}
											</div>
										</td>
										<td>
											Every {emp.weekend_frequency} weeks
										</td>
										<td>
											<span className='badge badge-secondary'>
												{emp.shift_type === "day"
													? "Day"
													: emp.shift_type ===
														  "evening"
														? "Evening"
														: "Night"}
											</span>
										</td>
										<td>
											<div className='flex gap-sm'>
												<button
													className='btn btn-sm btn-secondary'
													onClick={() =>
														openModal(emp)
													}
												>
													Edit
												</button>
												<button
													className='btn btn-sm btn-danger'
													onClick={() =>
														handleDelete(emp.id)
													}
												>
													Delete
												</button>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</div>

			{/* Employee Modal */}
			{showModal && (
				<div className='modal-overlay' onClick={closeModal}>
					<div
						className='modal'
						onClick={(e) => e.stopPropagation()}
						style={{ maxWidth: "600px" }}
					>
						<div className='modal-header'>
							<h3>
								{editingEmployee
									? "Edit Employee"
									: "Add Employee"}
							</h3>
							<button
								className='modal-close'
								onClick={closeModal}
							>
								&times;
							</button>
						</div>
						<form onSubmit={handleSubmit}>
							<div className='modal-body'>
								{error && (
									<div className='alert alert-error'>
										{error}
									</div>
								)}

								<div className='form-group'>
									<label className='form-label'>
										First Name *
									</label>
									<input
										type='text'
										className='form-input'
										value={formData.firstName}
										onChange={(e) =>
											setFormData({
												...formData,
												firstName: e.target.value,
											})
										}
										required
									/>
								</div>

								<div className='grid-2'>
									<div className='form-group'>
										<label className='form-label'>
											Username (for login)
										</label>
										<input
											type='text'
											className='form-input'
											autoComplete='off'
											value={formData.username}
											onChange={(e) =>
												setFormData({
													...formData,
													username: e.target.value,
												})
											}
										/>
									</div>
									<div className='form-group'>
										<label className='form-label'>
											{editingEmployee
												? "New Password"
												: "Password"}
										</label>
										<input
											type='password'
											className='form-input'
											autoComplete='new-password'
											placeholder={
												editingEmployee
													? editingEmployee.username
														? "Leave blank to keep current"
														: "Set a password to create a login"
													: ""
											}
											value={formData.password}
											onChange={(e) =>
												setFormData({
													...formData,
													password: e.target.value,
												})
											}
										/>
									</div>
								</div>
								{editingEmployee &&
									!editingEmployee.username && (
										<p
											className='text-sm text-muted'
											style={{ marginTop: "-0.5rem" }}
										>
											This employee has no login yet. Enter
											a username and password to create one.
										</p>
									)}

								<div className='form-group'>
									<label className='form-label'>
										Regular Shift
									</label>
									<select
										className='form-select'
										value={formData.shiftType}
										onChange={(e) =>
											setFormData({
												...formData,
												shiftType: e.target.value,
											})
										}
									>
										<option value='day'>Day Shift</option>
										<option value='evening'>
											Evening Shift
										</option>
										<option value='night'>
											Night Shift
										</option>
									</select>
								</div>

								<div className='form-group'>
									<label
										className='form-checkbox'
										style={{
											display: "flex",
											alignItems: "center",
											gap: "0.5rem",
											cursor: "pointer",
										}}
									>
										<input
											type='checkbox'
											checked={formData.isSupervisor}
											onChange={(e) =>
												setFormData({
													...formData,
													isSupervisor:
														e.target.checked,
												})
											}
										/>
										<span className='font-medium'>
											Supervisor
										</span>
										<span
											className='text-xs text-muted'
											style={{ marginLeft: "0.25rem" }}
										>
											(Assigned Administrative Duties when
											staffing is adequate)
										</span>
									</label>
								</div>

								<div className='form-group'>
									<label className='form-label'>
										Can Work In (Departments)
									</label>
									<div
										className='flex gap-sm'
										style={{ flexWrap: "wrap" }}
									>
										{departments.map((dept) => (
											<label
												key={dept.id}
												className='form-checkbox'
												style={{
													padding: "0.5rem 0.75rem",
													background:
														formData.departments.includes(
															dept.id,
														)
															? "var(--primary-100)"
															: "var(--gray-100)",
													borderRadius:
														"var(--radius-md)",
													cursor: "pointer",
												}}
											>
												<input
													type='checkbox'
													checked={formData.departments.includes(
														dept.id,
													)}
													onChange={() =>
														toggleDepartment(
															dept.id,
														)
													}
												/>
												{dept.name}
											</label>
										))}
										{departments.length === 0 && (
											<span className='text-muted text-sm'>
												No departments available. Create
												departments first.
											</span>
										)}
									</div>
								</div>

								<div className='form-group'>
									<label className='form-label'>
										Regular Days Off (2-Week Cycle)
									</label>
									<p className='text-xs text-muted mb-sm'>
										Select the days this employee has off in
										each week of the 2-week cycle.
									</p>

									<div
										style={{ display: "grid", gap: "1rem" }}
									>
										{[1, 2].map((weekNum) => (
											<div key={weekNum}>
												<div className='text-sm font-medium mb-sm'>
													Week {weekNum}
												</div>
												<div className='day-chips'>
													{DAYS_OF_WEEK.map(
														(day, idx) => (
															<button
																key={idx}
																type='button'
																className={`day-chip ${isDayOffSelected(weekNum, idx) ? "selected" : ""}`}
																onClick={() =>
																	toggleDayOff(
																		weekNum,
																		idx,
																	)
																}
															>
																{day}
															</button>
														),
													)}
												</div>
											</div>
										))}
									</div>
								</div>
							</div>
							<div className='modal-footer'>
								<button
									type='button'
									className='btn btn-secondary'
									onClick={closeModal}
								>
									Cancel
								</button>
								<button
									type='submit'
									className='btn btn-primary'
								>
									{editingEmployee
										? "Update Employee"
										: "Create Employee"}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}
		</div>
	);
}

export default EmployeeList;
